import { useLingui } from '@lingui/react/macro'
import { Text } from 'designSystem/components/Text'
import { MetadataFieldList } from 'features/knowledgeDocumentViewer/components/metadata/MetadataFieldList'
import type { DocumentMetadataUserEditValues } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { withoutDocumentRevisionFields } from 'features/knowledgeDocumentViewer/utils/documentMetadataFieldKeys'
import { getDocumentMetadataDisplayFields } from 'features/knowledgeDocumentViewer/utils/documentMetadataFields'
import { withDocumentTypeLabels } from 'features/knowledgeDocumentViewer/utils/documentTypeLabels'
import React, { useMemo } from 'react'

export type DocumentMetadataContentProps = {
  document: SystemDocumentEntry | undefined
  isLoading: boolean
  userEditValues?: DocumentMetadataUserEditValues
  includeInternalIds?: boolean
  hideRevision?: boolean
}

export function DocumentMetadataContent({
  document,
  isLoading,
  userEditValues,
  includeInternalIds = false,
  hideRevision = false,
}: DocumentMetadataContentProps): React.ReactElement {
  const { t, i18n } = useLingui()
  const fields = useMemo(() => {
    const displayFields = getDocumentMetadataDisplayFields(
      document,
      userEditValues,
      { includeInternalIds }
    )
    return withDocumentTypeLabels(
      hideRevision
        ? withoutDocumentRevisionFields(displayFields)
        : displayFields,
      (descriptor) => i18n._(descriptor)
    )
  }, [document, hideRevision, i18n, includeInternalIds, userEditValues])

  if (isLoading) {
    return (
      <Text variant='body' color='muted' italic>
        {t`Loading document details…`}
      </Text>
    )
  }

  if (fields.length === 0) {
    return (
      <Text variant='body' color='muted' italic>
        {t`No document details`}
      </Text>
    )
  }

  return <MetadataFieldList fields={fields} />
}
